import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../../api';
import { formatDateTime } from '../../utils/dateUtils';

/**
 * Google Sheets (CSV Publish) Setup Page
 *
 * 5-step wizard: connect → columns → rep mapping → KPI links → test/sync.
 * URL is masked after save — admins must re-type the full URL to change it.
 */

export default function GoogleSheetsSetupPage() {
  const navigate = useNavigate();
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Step 1: URL
  const [csvUrl, setCsvUrl] = useState('');
  const [urlConfigured, setUrlConfigured] = useState(false);
  const [csvUrlMasked, setCsvUrlMasked] = useState('');
  const [editingUrl, setEditingUrl] = useState(false);

  // Step 2: column mapping
  const [colDate, setColDate] = useState('Date Received');
  const [colRep, setColRep] = useState('Account Rep');
  const [colTowsYear, setColTowsYear] = useState('Tows in the Last Year');
  const [colNotes, setColNotes] = useState('Notes');
  const [skipNoNotice, setSkipNoNotice] = useState(false);

  // Step 3: rep mapping
  const [repMap, setRepMap] = useState({});
  const [localUsers, setLocalUsers] = useState([]);
  const [newRepName, setNewRepName] = useState('');
  const [newRepUserId, setNewRepUserId] = useState('');

  // Step 4: KPI links
  const [kpis, setKpis] = useState([]);
  const [accountsKpiId, setAccountsKpiId] = useState('');
  const [towsKpiId, setTowsKpiId] = useState('');
  const [syncEnabled, setSyncEnabled] = useState(false);

  // Test / preview / sync state
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [previewResult, setPreviewResult] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [cfg, kpiList, users] = await Promise.all([
        api.getGSheetsConfig(),
        api.getLeaderboardKpis(),
        api.getGSheetsUsers(),
      ]);
      setConfig(cfg);
      setCsvUrlMasked(cfg.csvUrlMasked || '');
      setUrlConfigured(cfg.csvUrlConfigured || false);
      setColDate(cfg.colDate || 'Date Received');
      setColRep(cfg.colRep || 'Account Rep');
      setColTowsYear(cfg.colTowsYear || 'Tows in the Last Year');
      setColNotes(cfg.colNotes || 'Notes');
      setSkipNoNotice(cfg.skipNoNotice || false);
      setRepMap(cfg.repMap || {});
      setAccountsKpiId(cfg.accountsKpiId ? String(cfg.accountsKpiId) : '');
      setTowsKpiId(cfg.towsKpiId ? String(cfg.towsKpiId) : '');
      setSyncEnabled(cfg.syncEnabled || false);
      setKpis(Array.isArray(kpiList) ? kpiList.filter(k => k.input_method === 'api' || k.input_method === 'manual') : []);
      setLocalUsers(Array.isArray(users) ? users : []);
    } catch { /* */ }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  // ── Save URL ──
  async function handleSaveUrl() {
    if (!csvUrl.trim()) { setError('URL required'); return; }
    setSaving(true);
    setError('');
    try {
      await api.saveGSheetsConfig({ csvUrl: csvUrl.trim() });
      setSuccess('URL saved (encrypted at rest)');
      setCsvUrl('');
      setEditingUrl(false);
      setTimeout(() => setSuccess(''), 3000);
      await load();
    } catch (err) {
      setError(err.message || 'Failed to save URL');
    }
    setSaving(false);
  }

  // ── Save Columns ──
  async function handleSaveColumns() {
    setSaving(true);
    setError('');
    try {
      await api.saveGSheetsConfig({
        colDate, colRep, colTowsYear, colNotes, skipNoNotice,
      });
      setSuccess('Column mapping saved');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.message || 'Failed to save columns');
    }
    setSaving(false);
  }

  // ── Rep Mapping ──
  function addRepMapping() {
    if (!newRepName.trim() || !newRepUserId) return;
    setRepMap(prev => ({ ...prev, [newRepName.trim()]: parseInt(newRepUserId) }));
    setNewRepName('');
    setNewRepUserId('');
  }

  function removeRepMapping(name) {
    setRepMap(prev => {
      const next = { ...prev };
      delete next[name];
      return next;
    });
  }

  async function handleSaveRepMap() {
    setSaving(true);
    setError('');
    try {
      await api.saveGSheetsConfig({ repMap });
      setSuccess('Rep mapping saved');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.message || 'Failed to save rep mapping');
    }
    setSaving(false);
  }

  // ── Save KPIs + sync toggle ──
  async function handleSaveKpis() {
    setSaving(true);
    setError('');
    try {
      await api.saveGSheetsConfig({
        accountsKpiId: accountsKpiId ? parseInt(accountsKpiId) : null,
        towsKpiId: towsKpiId ? parseInt(towsKpiId) : null,
        syncEnabled,
      });
      setSuccess('KPI links + sync settings saved');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.message || 'Failed to save KPI links');
    }
    setSaving(false);
  }

  // ── Test connection ──
  async function handleTest() {
    setTesting(true);
    setError('');
    setTestResult(null);
    try {
      const r = await api.testGSheets();
      setTestResult(r);
      setSuccess(`Fetched ${r.rowCount} rows`);
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.message || 'Test failed');
    }
    setTesting(false);
  }

  // ── Preview aggregation ──
  async function handlePreview() {
    setPreviewing(true);
    setError('');
    setPreviewResult(null);
    try {
      const r = await api.previewGSheets();
      setPreviewResult(r);
    } catch (err) {
      setError(err.message || 'Preview failed');
    }
    setPreviewing(false);
  }

  // ── Manual sync ──
  async function handleSync() {
    setSyncing(true);
    setError('');
    try {
      const r = await api.syncGSheets();
      if (r.synced) {
        const parts = [];
        if (r.accounts?.kpiName) parts.push(`Accounts: ${r.accounts.repsUpdated} reps / ${r.accounts.totalAccountsLost} lost`);
        if (r.tows?.kpiName) parts.push(`Tows: ${r.tows.repsUpdated} reps / ${r.tows.totalTowsWalked} walked`);
        setSuccess(`Sync complete (${r.period}) — ${parts.join(' · ')}`);
        await load();
      } else {
        setError(r.reason || 'Sync not ready');
      }
      setTimeout(() => setSuccess(''), 6000);
    } catch (err) {
      setError(err.message || 'Sync failed');
    }
    setSyncing(false);
  }

  // ── Disconnect ──
  async function handleDisconnect() {
    if (!window.confirm('Disconnect Google Sheets? This will clear the URL, rep mapping, and all config. You can reconnect anytime by re-entering your URL.')) return;
    setDisconnecting(true);
    try {
      await api.disconnectGSheets();
      setSuccess('Disconnected. All config cleared.');
      await load();
      setTimeout(() => setSuccess(''), 4000);
    } catch (err) {
      setError(err.message || 'Disconnect failed');
    }
    setDisconnecting(false);
  }

  if (loading) return <div className="admin-page"><div className="loading-spinner" /></div>;

  const lastSync = config?.lastSync ? formatDateTime(config.lastSync) : 'Never';
  const lastResult = config?.lastSyncResult;

  return (
    <div className="admin-page">
      <div className="maint-types__header">
        <h1 style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ fontSize: 26 }}>📊</span>
          Google Sheets Integration
        </h1>
        <button className="btn btn-outline" onClick={() => navigate('/admin-mod/leaderboard-integrations')}>
          ← Back to Integrations
        </button>
      </div>
      <p style={{ color: '#64748b', fontSize: 13, marginBottom: 20 }}>
        Pull KPI values from a Google Sheet published as CSV. The URL is encrypted at rest and never exposed after saving.
      </p>

      {error && <div className="alert alert-error" style={{ marginBottom: 16 }}>{error}</div>}
      {success && <div className="alert alert-success" style={{ marginBottom: 16 }}>{success}</div>}
      {config?.lastError && (
        <div className="alert" style={{ marginBottom: 16, background: '#fef3c7', color: '#92400e', border: '1px solid #fcd34d', padding: 12, borderRadius: 8, fontSize: 12 }}>
          ⚠️ Last fetch error: <b>{config.lastError}</b>
          {config.lastErrorAt && <span> &middot; {formatDateTime(config.lastErrorAt)}</span>}
        </div>
      )}

      {/* ─── STEP 1: CSV URL ─── */}
      <Section num={1} title="Publish Sheet as CSV + Connect">
        <p style={{ fontSize: 12, color: '#64748b', marginBottom: 12 }}>
          In your Google Sheet: <b>File → Share → Publish to web</b>. Pick the correct tab, choose <b>Comma-separated values (.csv)</b>, click Publish. Paste the resulting URL below.
        </p>

        {urlConfigured && !editingUrl ? (
          <>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: 12, background: '#f0fdf4', border: '1px solid #86efac', borderRadius: 8, marginBottom: 10 }}>
              <span style={{ fontSize: 11, fontWeight: 800, color: '#15803d', padding: '3px 10px', borderRadius: 6, background: '#dcfce7' }}>CONNECTED (ENCRYPTED)</span>
              <code style={{ flex: 1, fontSize: 11, color: '#475569', fontFamily: 'monospace', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {csvUrlMasked}
              </code>
            </div>
            <button className="btn btn-outline" onClick={() => setEditingUrl(true)} style={{ fontSize: 12 }}>
              Change URL
            </button>
          </>
        ) : (
          <>
            <div className="form-group">
              <label>Published CSV URL</label>
              <input
                type="text"
                className="form-input"
                value={csvUrl}
                onChange={e => setCsvUrl(e.target.value)}
                placeholder="https://docs.google.com/spreadsheets/d/e/2PACX-1v.../pub?output=csv"
                style={{ fontFamily: 'monospace', fontSize: 12 }}
              />
            </div>
            <div style={{ display: 'flex', gap: 10 }}>
              <button className="btn btn-gold" onClick={handleSaveUrl} disabled={saving || !csvUrl.trim()}>
                {saving ? 'Saving...' : 'Save URL'}
              </button>
              {editingUrl && (
                <button className="btn btn-outline" onClick={() => { setEditingUrl(false); setCsvUrl(''); }}>
                  Cancel
                </button>
              )}
            </div>
          </>
        )}
      </Section>

      {/* ─── STEP 2: Columns ─── */}
      <Section num={2} title="Column Mapping" disabled={!urlConfigured}>
        <p style={{ fontSize: 12, color: '#64748b', marginBottom: 12 }}>
          Tell us which column headers to read. Must match EXACTLY (case-sensitive).
        </p>
        <div className="form-grid">
          <div className="form-group">
            <label>Date Received Column</label>
            <input className="form-input" value={colDate} onChange={e => setColDate(e.target.value)} />
          </div>
          <div className="form-group">
            <label>Account Rep Column</label>
            <input className="form-input" value={colRep} onChange={e => setColRep(e.target.value)} />
          </div>
          <div className="form-group">
            <label>Tows in the Last Year Column</label>
            <input className="form-input" value={colTowsYear} onChange={e => setColTowsYear(e.target.value)} />
          </div>
          <div className="form-group">
            <label>Notes Column</label>
            <input className="form-input" value={colNotes} onChange={e => setColNotes(e.target.value)} />
          </div>
        </div>
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', marginTop: 8 }}>
          <input type="checkbox" checked={skipNoNotice} onChange={e => setSkipNoNotice(e.target.checked)} style={{ width: 16, height: 16 }} />
          <span style={{ fontSize: 13, color: '#0f172a', fontWeight: 600 }}>Skip rows where Notes contains "No Cancellation Sent"</span>
        </label>
        <div style={{ marginTop: 12, display: 'flex', gap: 10 }}>
          <button className="btn btn-gold" onClick={handleSaveColumns} disabled={saving}>
            {saving ? 'Saving...' : 'Save Column Mapping'}
          </button>
          <button className="btn btn-outline" onClick={handleTest} disabled={testing || !urlConfigured}>
            {testing ? 'Testing...' : 'Test Connection'}
          </button>
        </div>

        {testResult && (
          <div style={{ marginTop: 12, padding: 12, background: '#f0fdf4', border: '1px solid #86efac', borderRadius: 8, fontSize: 12 }}>
            <div><b>✅ Connected.</b> Fetched {testResult.rowCount} rows.</div>
            {testResult.headers && (
              <div style={{ marginTop: 6, color: '#475569' }}>
                <b>Detected headers:</b> {testResult.headers.join(' · ')}
              </div>
            )}
          </div>
        )}
      </Section>

      {/* ─── STEP 3: Rep Mapping ─── */}
      <Section num={3} title="Map Sheet Reps to Dispatch Users" disabled={!urlConfigured}>
        <p style={{ fontSize: 12, color: '#64748b', marginBottom: 12 }}>
          For each rep name that appears in the sheet's Account Rep column, pick the matching dispatch user. Reps without a mapping are skipped during sync.
        </p>

        {Object.keys(repMap).length > 0 && (
          <div style={{ border: '1px solid #e2e8f0', borderRadius: 8, overflow: 'hidden', marginBottom: 12 }}>
            {Object.entries(repMap).map(([sheetName, userId]) => {
              const user = localUsers.find(u => u.id === parseInt(userId));
              return (
                <div key={sheetName} style={{ display: 'flex', alignItems: 'center', padding: '10px 12px', borderBottom: '1px solid #f1f5f9' }}>
                  <div style={{ flex: 1, fontSize: 13 }}>
                    <b>{sheetName}</b> <span style={{ color: '#94a3b8' }}>→</span> {user ? user.full_name : <span style={{ color: '#dc2626' }}>User not found</span>}
                  </div>
                  <button className="btn btn-outline" onClick={() => removeRepMapping(sheetName)} style={{ fontSize: 11, padding: '4px 10px' }}>Remove</button>
                </div>
              );
            })}
          </div>
        )}

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr auto', gap: 10, alignItems: 'end' }}>
          <div className="form-group" style={{ margin: 0 }}>
            <label>Sheet rep name</label>
            <input className="form-input" value={newRepName} onChange={e => setNewRepName(e.target.value)} placeholder="e.g., Frank" />
          </div>
          <div className="form-group" style={{ margin: 0 }}>
            <label>Dispatch user</label>
            <select className="form-input" value={newRepUserId} onChange={e => setNewRepUserId(e.target.value)}>
              <option value="">— Select user —</option>
              {localUsers.map(u => (
                <option key={u.id} value={u.id}>{u.full_name} ({u.role})</option>
              ))}
            </select>
          </div>
          <button className="btn btn-outline" onClick={addRepMapping} disabled={!newRepName.trim() || !newRepUserId}>
            + Add
          </button>
        </div>

        <div style={{ marginTop: 12 }}>
          <button className="btn btn-gold" onClick={handleSaveRepMap} disabled={saving}>
            {saving ? 'Saving...' : 'Save Rep Mapping'}
          </button>
        </div>
      </Section>

      {/* ─── STEP 4: KPI Links + Sync ─── */}
      <Section num={4} title="Link KPIs + Enable Sync" disabled={!urlConfigured}>
        <p style={{ fontSize: 12, color: '#64748b', marginBottom: 12 }}>
          Create two API-type KPIs in KPI Setup first (yearly period, "negative" sign recommended). Then link them here.
        </p>

        <div className="form-grid">
          <div className="form-group">
            <label>➖ Accounts Lost KPI</label>
            <select className="form-input" value={accountsKpiId} onChange={e => setAccountsKpiId(e.target.value)}>
              <option value="">— None —</option>
              {kpis.map(k => (
                <option key={k.id} value={k.id}>{k.name} ({k.period_type})</option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label>➖ Tows Walked KPI</label>
            <select className="form-input" value={towsKpiId} onChange={e => setTowsKpiId(e.target.value)}>
              <option value="">— None —</option>
              {kpis.map(k => (
                <option key={k.id} value={k.id}>{k.name} ({k.period_type})</option>
              ))}
            </select>
          </div>
        </div>

        <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', marginTop: 8 }}>
          <input type="checkbox" checked={syncEnabled} onChange={e => setSyncEnabled(e.target.checked)} style={{ width: 18, height: 18 }} />
          <span style={{ fontWeight: 600, fontSize: 14 }}>Enable auto-sync every 15 minutes</span>
        </label>

        <div style={{ marginTop: 12, display: 'flex', gap: 10 }}>
          <button className="btn btn-gold" onClick={handleSaveKpis} disabled={saving}>
            {saving ? 'Saving...' : 'Save KPI Links + Sync'}
          </button>
          <button className="btn btn-outline" onClick={handlePreview} disabled={previewing || !urlConfigured}>
            {previewing ? 'Loading...' : 'Preview YTD'}
          </button>
          <button className="btn btn-outline" onClick={handleSync} disabled={syncing || (!accountsKpiId && !towsKpiId)}>
            {syncing ? 'Syncing...' : 'Sync Now'}
          </button>
        </div>

        {previewResult && (
          <div style={{ marginTop: 12, padding: 14, background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 12 }}>
            <div style={{ marginBottom: 8, fontWeight: 700, color: '#0f172a' }}>YTD Preview</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
              <div>
                <div style={{ fontSize: 11, fontWeight: 700, color: '#64748b', marginBottom: 4 }}>ACCOUNTS LOST</div>
                {Object.entries(previewResult.accountsLost).length === 0 ? (
                  <div style={{ color: '#94a3b8' }}>No data</div>
                ) : Object.entries(previewResult.accountsLost).map(([uid, count]) => (
                  <div key={uid}>{previewResult.userNames[uid] || `User #${uid}`}: <b>{count}</b></div>
                ))}
              </div>
              <div>
                <div style={{ fontSize: 11, fontWeight: 700, color: '#64748b', marginBottom: 4 }}>TOWS WALKED (365d)</div>
                {Object.entries(previewResult.towsWalked).length === 0 ? (
                  <div style={{ color: '#94a3b8' }}>No data</div>
                ) : Object.entries(previewResult.towsWalked).map(([uid, sum]) => (
                  <div key={uid}>{previewResult.userNames[uid] || `User #${uid}`}: <b>{sum}</b></div>
                ))}
              </div>
            </div>
            <div style={{ marginTop: 10, paddingTop: 10, borderTop: '1px solid #e2e8f0', color: '#64748b', fontSize: 11 }}>
              {previewResult.totalRows} total rows &middot; {previewResult.processedRows} processed
              {previewResult.ignoredDateMissing > 0 && <> &middot; {previewResult.ignoredDateMissing} date missing</>}
              {previewResult.ignoredDateUnparseable > 0 && <> &middot; {previewResult.ignoredDateUnparseable} date unparseable</>}
              {previewResult.ignoredOutOfRange > 0 && <> &middot; {previewResult.ignoredOutOfRange} out-of-year</>}
              {previewResult.ignoredNoNotice > 0 && <> &middot; {previewResult.ignoredNoNotice} "no notice" skipped</>}
              {previewResult.unmappedRepNames?.length > 0 && (
                <div style={{ color: '#b45309', marginTop: 4 }}>
                  <b>Unmapped reps:</b> {previewResult.unmappedRepNames.join(', ')} — add them in Step 3
                </div>
              )}
            </div>

            {/* ─── Diagnostic section when nothing processed ─── */}
            {previewResult.processedRows === 0 && previewResult.totalRows > 0 && (
              <div style={{ marginTop: 12, padding: 12, background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, fontSize: 11 }}>
                <div style={{ fontWeight: 800, color: '#991b1b', marginBottom: 8, fontSize: 12 }}>
                  ⚠️ No rows processed — here's what's going wrong:
                </div>

                <div style={{ marginBottom: 10 }}>
                  <div style={{ fontWeight: 700, color: '#475569', marginBottom: 4 }}>Your column config:</div>
                  <div style={{ fontFamily: 'monospace', background: '#fff', padding: 8, borderRadius: 6, border: '1px solid #fecaca' }}>
                    Date: <b>"{previewResult.configuredDateColumn}"</b><br/>
                    Rep: <b>"{previewResult.configuredRepColumn}"</b><br/>
                    Tows/Year: <b>"{previewResult.configuredTowsYearColumn}"</b>
                  </div>
                </div>

                <div style={{ marginBottom: 10 }}>
                  <div style={{ fontWeight: 700, color: '#475569', marginBottom: 4 }}>Detected headers in CSV:</div>
                  <div style={{ fontFamily: 'monospace', background: '#fff', padding: 8, borderRadius: 6, border: '1px solid #fecaca', wordBreak: 'break-all' }}>
                    {previewResult.detectedHeaders?.map(h => `"${h}"`).join(' · ') || '(none)'}
                  </div>
                  {previewResult.detectedHeaders && !previewResult.detectedHeaders.includes(previewResult.configuredDateColumn) && (
                    <div style={{ color: '#dc2626', marginTop: 4 }}>
                      ❌ The Date column <code>"{previewResult.configuredDateColumn}"</code> is NOT in the detected headers. Fix it in Step 2.
                    </div>
                  )}
                </div>

                <div>
                  <div style={{ fontWeight: 700, color: '#475569', marginBottom: 4 }}>First 3 rejected rows:</div>
                  <pre style={{
                    background: '#fff', padding: 8, borderRadius: 6, border: '1px solid #fecaca',
                    fontSize: 10, margin: 0, overflow: 'auto', maxHeight: 300,
                  }}>
                    {JSON.stringify(previewResult.rejectSamples, null, 2)}
                  </pre>
                </div>
              </div>
            )}
          </div>
        )}
      </Section>

      {/* ─── Last Sync Info ─── */}
      {config && (
        <div style={{ background: '#f8fafc', borderRadius: 10, padding: 16, fontSize: 12, color: '#64748b', marginTop: 24 }}>
          <div><b>Last sync:</b> {lastSync}</div>
          {lastResult && (
            <div style={{ marginTop: 6 }}>
              <b>Period:</b> {lastResult.period}
              {lastResult.accounts?.kpiName && (
                <span> &middot; <span style={{ color: '#b91c1c', fontWeight: 700 }}>Accounts Lost:</span> {lastResult.accounts.repsUpdated} reps / {lastResult.accounts.totalAccountsLost}</span>
              )}
              {lastResult.tows?.kpiName && (
                <span> &middot; <span style={{ color: '#b91c1c', fontWeight: 700 }}>Tows Walked:</span> {lastResult.tows.repsUpdated} reps / {lastResult.tows.totalTowsWalked}</span>
              )}
            </div>
          )}
        </div>
      )}

      {/* ─── Disconnect ─── */}
      {urlConfigured && (
        <div style={{ marginTop: 24, padding: 16, background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 10 }}>
          <div style={{ fontSize: 13, fontWeight: 800, color: '#991b1b', marginBottom: 4 }}>Danger Zone</div>
          <div style={{ fontSize: 11, color: '#7f1d1d', marginBottom: 10 }}>
            Disconnect clears the URL, rep mapping, and all sync config. Existing leaderboard values already synced are NOT deleted. You can always reconnect by re-entering your URL.
          </div>
          <button
            className="btn btn-outline"
            onClick={handleDisconnect}
            disabled={disconnecting}
            style={{ borderColor: '#dc2626', color: '#dc2626' }}
          >
            {disconnecting ? 'Disconnecting...' : 'Disconnect Google Sheets'}
          </button>
        </div>
      )}
    </div>
  );
}

function Section({ num, title, disabled, children }) {
  return (
    <div style={{
      background: '#fff',
      borderRadius: 14,
      border: '1px solid #e2e8f0',
      padding: 24,
      marginBottom: 20,
      opacity: disabled ? 0.5 : 1,
      pointerEvents: disabled ? 'none' : 'auto',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
        <span style={{
          width: 28, height: 28, borderRadius: 8,
          background: '#16a34a15', color: '#16a34a',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: 13, fontWeight: 900,
        }}>{num}</span>
        <h2 style={{ margin: 0, fontSize: 16, fontWeight: 800 }}>{title}</h2>
      </div>
      {children}
    </div>
  );
}
